import { ClientesService } from 'app/services/clientes.service';
import { Component, OnInit } from '@angular/core';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Client } from 'app/models/client.models';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ParametrosService } from 'app/services/parametros.service';
import { ActivatedRoute, Router } from '@angular/router';
import { typeResponsble } from 'app/models/tiporesponsable.models';
import { DocumentTypes } from 'app/models/documenttype.models';
import { Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { AddressAutocompleteService } from 'app/services/address-autocomplete.service';
import { DocumentValidatorService } from 'app/services/document-validator.service';

// Interfaz para las predicciones de dirección
interface AddressPrediction {
  description: string;
  place_id: string;
}

@Component({
  selector: 'app-detalle-cliente',
  templateUrl: './detalle-cliente.component.html',
  styleUrls: ['./detalle-cliente.component.css'],
})
export class DetalleClienteComponent implements OnInit {
  responsables: typeResponsble[] = [];
  documentos: DocumentTypes[] = [];
  form: UntypedFormGroup;
  cliente: Client;
  textoBtn: string;
  idCliente: number;
  isReadOnly: boolean = false;
  searchTerm = new FormControl('');
  private searchSubject = new Subject<string>();
  predictions: AddressPrediction[] = [];
  addressInfo = {
    locality: '',
    province: '',
    country: '',
    longitude: 0,
    latitude: 0,
  };

  constructor(
    private clienteService: ClientesService,
    private parametroServicio: ParametrosService,
    private fb: UntypedFormBuilder,
    private _snackBar: MatSnackBar,
    private routerActive: ActivatedRoute,
    private router: Router,
    private addressService: AddressAutocompleteService
  ) {
    this.idCliente = this.routerActive.snapshot.params.id;

    if (!this.idCliente) {
      this.textoBtn = 'Crear';
      this.cliente = new Client();
      this.cliente.type = 'Cliente';
    } else {
      this.textoBtn = 'Actualizar';
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.getParametros();
    this.setupSearch();
  }

  initForm() {
    this.form = this.fb.group({
      number: ['', Validators.required],
      companyName: ['', Validators.required],
      type: ['cliente', Validators.required],
      documentNumber: ['', [Validators.required, DocumentValidatorService.validateArgentineDocument]],
      address: [''],
      email: [''],
      phone: [''],
      cellphone: [''],
      observations: [''],
      postalCode: [''],
      responsibleTypeId: ['', Validators.required],
      documentTypeId: ['', Validators.required],
      bonificacionValue: [0],
    });

    // Suscribirse a cambios en el tipo de documento para revalidar
    this.form.get('documentTypeId')?.valueChanges.subscribe(() => {
      this.onDocumentTypeChange();
    });
  }

  /**
   * Se ejecuta cuando cambia el tipo de documento seleccionado
   * Revalida el campo documentNumber para actualizar la validación cruzada
   */
  onDocumentTypeChange() {
    const documentNumberControl = this.form.get('documentNumber');
    if (documentNumberControl && documentNumberControl.value) {
      // Forzar revalidación del campo
      documentNumberControl.updateValueAndValidity();
      // Marcar como touched para mostrar validaciones visuales
      documentNumberControl.markAsTouched();
    }
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 2000,
    });
  }

  getParametros() {
    this.parametroServicio.getParametrosClientes().subscribe(data => {
      this.responsables = data.tiporesponsables;
      this.documentos = data.tipodocumentos;
      let responsable = this.responsables.find(x => x.code == '1');
      let documento = this.documentos.find(x => x.code == '80');
      const codigo = data.nuevoCodigo;
      this.form.controls.number.setValue(codigo);
      this.form.controls.number.disable();
      this.form.controls.responsibleTypeId.setValue(responsable.id, {
        onlySelf: true,
      });
      this.form.controls.documentTypeId.setValue(documento.id, {
        onlySelf: true,
      });
      // buscamos los datos del cliente si es que se esta editando
      if (this.idCliente) {
        this.getClienteById(this.idCliente);
      }
    });
  }

  getClienteById(id: number) {
    this.clienteService.getClienteById(id).subscribe({
      next: data => {
        this.cliente = data;
        this.searchTerm.patchValue(this.cliente.address);
        this.form.patchValue(this.cliente);
        
        // Desactivar el campo de número para todos los clientes existentes
        this.form.get('number').disable();
        
        // Si el cliente tiene ID 1, poner en modo solo lectura
        if (this.cliente.id === 1) {
          this.isReadOnly = true;
          this.textoBtn = 'Volver';
          this.searchTerm.disable();
          
          // Deshabilitar todos los campos del formulario
          Object.keys(this.form.controls).forEach(controlName => {
            this.form.get(controlName).disable();
          });
        }
      },
      error: err => {
        console.error(err);
        this.openSnackBar('Error al cargar el cliente', 'Error');
      },
    });
  }

  async guardarCliente() {
    // Si estamos en modo solo lectura (cliente con ID = 1), simplemente volver
    if (this.isReadOnly) {
      this.router.navigate(['/clientes']);
      return;
    }
    
    if (this.form.valid) {
      // Verificar que no se haya manipulado el número del cliente existente
      if (this.cliente?.id && this.cliente.number !== this.form.getRawValue().number) {
        this.openSnackBar('No se permite modificar el número del cliente', 'Error');
        // Restauramos el valor original
        this.form.get('number').setValue(this.cliente.number);
        return;
      }
      
      const clienteData: Client = {
        //id: this.cliente?.id,
        number: this.form.getRawValue().number,
        companyName: this.form.getRawValue().companyName,
        observations: this.form.value.observations,
        documentNumber: String(this.form.value.documentNumber).replace(/[.\-\s]/g, ''), // Eliminar puntos, guiones y espacios
        address: this.form.value.address || this.searchTerm.value,
        email: this.form.value.email,
        phone: String(this.form.value.phone),
        cellphone: String(this.form.value.cellphone),
        state: this.form.value.state,
        postalCode: String(this.form.value.postalCode),
        type: this.form.value.type,
        documentTypeId: this.form.value.documentTypeId,
        responsibleTypeId: this.form.value.responsibleTypeId,
        bonificacionValue: this.form.value.bonificacionValue,
      };

      if (this.searchTerm.value != '') {
        try {
          const geoData = await this.addressService.getGeocode(this.searchTerm.value);
          clienteData.country = geoData.country;
          clienteData.locality = geoData.locality;
          clienteData.province = geoData.province;
          clienteData.address = this.searchTerm.value;
          clienteData.latitude = geoData.latitude;
          clienteData.longitude = geoData.longitude;
        } catch (error) {
          console.error('Error obteniendo geocodificación:', error);
          clienteData.country = 'Argentina';
          clienteData.locality = '';
          clienteData.province = 'Misiones';
          clienteData.address = this.searchTerm.value;
          clienteData.latitude = 0;
          clienteData.longitude = 0;
        }
      } else {
        clienteData.country = 'Argentina';
        clienteData.locality = '';
        clienteData.province = 'Misiones';
        clienteData.address = 'Misiones, Argentina';
        clienteData.latitude = 0;
        clienteData.longitude = 0;
      }

      if (this.cliente?.id) {
        this.clienteService.actualizarCliente(this.cliente.id, clienteData).subscribe({
          next: () => {
            this.openSnackBar(
              `${clienteData.type} actualizado correctamente`,
              'Información'
            );
            this.router.navigate(['/clientes']);
          },
          error: err => {
            console.error(err);
            this.openSnackBar(`Error al actualizar ${clienteData.type}`, 'Error');
          },
        });
      } else {
        this.clienteService.guardarCliente(clienteData).subscribe({
          next: () => {
            this.openSnackBar(
              `${clienteData.type} guardado correctamente`,
              'Información'
            );
            this.router.navigate(['/clientes']);
          },
          error: err => {
            console.error(err);
            this.openSnackBar(`Error al guardar ${clienteData.type}`, 'Error');
          },
        });
      }
    } else {
      this.openSnackBar('Por favor complete los campos requeridos', 'Error');
    }
  }

  // Este método permite editar el número solo para clientes nuevos
  editarNumero() {
    // Solo permitir editar el número para clientes nuevos
    if (!this.cliente?.id) {
      if (this.form.controls.number.disabled) {
        this.form.controls.number.enable();
      } else {
        this.form.controls.number.disable();
      }
    } else {
      this.openSnackBar('No se permite modificar el número de un cliente existente', 'Información');
    }
  }

  onSearch() {
    if (this.searchTerm.value) {
      this.searchSubject.next(this.searchTerm.value);
    }
  }

  setupSearch() {
    this.searchSubject.pipe(
      debounceTime(300),
      switchMap(term => this.addressService.getSuggestions(term))
    ).subscribe(results => {
      this.predictions = results;
    });
  }

  onAddressSelect(selectedAddress: string) {
    this.searchTerm.setValue(selectedAddress);
    this.form.get('address').setValue(selectedAddress);
    this.predictions = [];
  }

  // Permitir solo números, guiones y espacios en el input (teclado)
  onlyNumberKey(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    
    // Permitir teclas especiales (backspace, delete, tab, escape, enter, arrow keys, etc.)
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(charCode) !== -1) {
      return true;
    }
    
    // Permitir guiones (-) y espacios
    if (charCode === 45 || charCode === 32) {
      return true;
    }
    
    // Verificar si ya se alcanzó el límite máximo de 11 dígitos (solo números)
    const currentValue = this.form.get('documentNumber')?.value || '';
    const cleanValue = currentValue.replace(/\D/g, ''); // Solo números
    const target = event.target as HTMLInputElement;
    const selectionStart = target.selectionStart || 0;
    const selectionEnd = target.selectionEnd || 0;
    
    // Si hay selección, se reemplazará, así que es válido
    // Si no hay selección y ya tenemos 11 números, bloquear solo si es un número
    if (selectionStart === selectionEnd && cleanValue.length >= 11 && (charCode >= 48 && charCode <= 57)) {
      event.preventDefault();
      return false;
    }
    
    // Permite solo números (0-9)
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    
    return true;
  }

  // Evitar pegar caracteres no válidos en el input
  preventNonNumericPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedInput: string = clipboardData.getData('text');
    
    // Verificar si solo contiene números, guiones y espacios
    if (!/^[0-9\-\s]+$/.test(pastedInput)) {
      event.preventDefault();
      this.openSnackBar('Solo se permiten números, guiones y espacios en este campo', 'Error');
      return false;
    }
    
    // Limpiar el contenido pegado (solo números) para verificar longitud
    const cleanPastedInput = pastedInput.replace(/\D/g, '');
    
    // Verificar la longitud máxima
    const currentValue = this.form.get('documentNumber')?.value || '';
    const cleanCurrentValue = currentValue.replace(/\D/g, '');
    const selectionStart = (event.target as HTMLInputElement).selectionStart || 0;
    const selectionEnd = (event.target as HTMLInputElement).selectionEnd || 0;
    
    // Calcular el nuevo valor después del pegado (solo números)
    const beforeSelection = cleanCurrentValue.substring(0, this.getCleanPosition(currentValue, selectionStart));
    const afterSelection = cleanCurrentValue.substring(this.getCleanPosition(currentValue, selectionEnd));
    const newCleanValue = beforeSelection + cleanPastedInput + afterSelection;
    
    if (newCleanValue.length > 11) {
      event.preventDefault();
      this.openSnackBar('El documento no puede tener más de 11 dígitos', 'Error');
      return false;
    }
    
    return true;
  }

  // Método auxiliar para obtener la posición en la cadena limpia (solo números)
  private getCleanPosition(value: string, position: number): number {
    let cleanPosition = 0;
    for (let i = 0; i < position && i < value.length; i++) {
      if (/\d/.test(value[i])) {
        cleanPosition++;
      }
    }
    return cleanPosition;
  }

  // Obtener el mensaje de error de validación del documento
  getDocumentErrorMessage(): string {
    const control = this.form.get('documentNumber');
    if (control && control.errors && control.touched) {
      if (control.errors['required']) {
        return 'El número de documento es requerido';
      }
      if (control.errors['invalidDocument']) {
        return control.errors['invalidDocument'].message;
      }
    }
    return '';
  }

  // Formatear el documento mientras el usuario escribe
  onDocumentNumberChange(event: any) {
    const inputElement = event.target;
    const value = inputElement.value;
    const cursorPosition = inputElement.selectionStart;
    
    // Solo limpiar caracteres no numéricos pero permitir entrada con guiones
    let cleanValue = value.replace(/\D/g, '');
    
    // Limitar a máximo 11 dígitos
    if (cleanValue.length > 11) {
      cleanValue = cleanValue.substring(0, 11);
    }
    
    // Solo actualizar si el valor limpio cambió
    if (cleanValue !== value.replace(/\D/g, '')) {
      // Calcular la nueva posición del cursor
      let newCursorPosition = cursorPosition;
      if (cleanValue.length < value.replace(/\D/g, '').length) {
        // Se eliminaron dígitos, ajustar cursor
        newCursorPosition = Math.min(cursorPosition, cleanValue.length);
      }
      
      this.form.get('documentNumber')?.setValue(cleanValue, { emitEvent: false });
      
      // Restaurar posición del cursor
      setTimeout(() => {
        inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
  }

  // Formatear el documento cuando el usuario sale del campo
  onDocumentNumberBlur(event: any) {
    const value = event.target.value;
    if (value && value.length > 0) {
      const formatted = DocumentValidatorService.formatDocument(value);
      this.form.get('documentNumber').setValue(formatted, { emitEvent: false });
    }
  }

  // Obtener el tipo de documento detectado
  getDetectedDocumentType(): string {
    const documentNumber = this.form.get('documentNumber')?.value;
    if (documentNumber) {
      return DocumentValidatorService.getDocumentTypeWithHeuristics(documentNumber);
    }
    return '';
  }

  // Obtener el tipo de documento detectado sin heurísticas (original)
  getDetectedDocumentTypeOriginal(): string {
    const documentNumber = this.form.get('documentNumber')?.value;
    if (documentNumber) {
      return DocumentValidatorService.getDocumentType(documentNumber);
    }
    return '';
  }

  // Verificar si el documento es válido
  isDocumentValid(): boolean {
    const control = this.form.get('documentNumber');
    return !!(control && control.value && control.valid && control.touched);
  }

  // Verificar si el documento es inválido
  isDocumentInvalid(): boolean {
    const control = this.form.get('documentNumber');
    return !!(control && control.value && control.invalid && control.touched);
  }

  // Verificar si debe mostrar el ícono de validación
  shouldShowValidationIcon(): boolean {
    const control = this.form.get('documentNumber');
    return !!(control && control.value && control.touched);
  }

  // Obtener el tipo seleccionado en el dropdown del tipo de documento
  getSelectedDocumentType(): string {
    const documentTypeId = this.form.get('documentTypeId')?.value;
    const selectedType = this.documentos.find(doc => doc.id === documentTypeId);
    return selectedType ? selectedType.code.toString() : '';
  }

  // Obtener el nombre del tipo de documento seleccionado
  getSelectedDocumentTypeName(): string {
    const documentTypeId = this.form.get('documentTypeId')?.value;
    const selectedType = this.documentos.find(doc => doc.id === documentTypeId);
    return selectedType ? selectedType.name.toString() : '';
  }

  // Verificar si el documento ingresado coincide con el tipo seleccionado
  documentMatchesSelectedType(): boolean {
    const detectedType = this.getDetectedDocumentType();
    const selectedTypeCode = this.getSelectedDocumentType();
    
    if (!detectedType || !selectedTypeCode) return false;

    // Mapear códigos AFIP a tipos detectados
    const typeMapping: { [key: string]: string[] } = {
      '80': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // DNI (pero acepta todos los documentos argentinos válidos)
      '86': ['CUIL', 'CUIL/CUIT'], // CUIL (acepta también ambiguos)
      '87': ['CUIT', 'CUIL/CUIT'], // CUIT (acepta también ambiguos)
      '96': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // Pasaporte (acepta todos)
      '89': ['CUIL', 'CUIT', 'CUIL/CUIT'], // Libreta de Enrolamiento (acepta CUIL/CUIT)
      '90': ['CUIL', 'CUIT', 'CUIL/CUIT'], // Libreta Cívica (acepta CUIL/CUIT)
      '91': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // Documento extranjero (acepta todos)
      '92': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // Documento extranjero - F. 150 (acepta todos)
      '93': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // Documento extranjero - F. 211 (acepta todos)
      '94': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // Documento extranjero - F. 743 (acepta todos)
      '95': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // Certificado de residencia (acepta todos)
      '00': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // CI Policia Federal (acepta cualquiera)
      '01': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // CI Buenos Aires (acepta cualquiera)
      '02': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // CI Catamarca (acepta cualquiera)
      '03': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // CI Córdoba (acepta cualquiera)
      '04': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // CI Corrientes (acepta cualquiera)
      '05': ['DNI', 'CUIL', 'CUIT', 'CUIL/CUIT'], // CI Entre Ríos (acepta cualquiera)
      // Agregar más códigos según los disponibles en el sistema
    };

    const validTypes = typeMapping[selectedTypeCode] || [];
    return validTypes.includes(detectedType);
  }
}
